import apiTamboUrl from "@/services/api";
import { TARJETAS_DASHBOARD_CRIA } from "@/utils/cria/tarjetasCriaHelper";
import db from "@/services/offlineDb";
import { enqueueOp } from "@/services/offlineQueue";
import { normalizarCaravana } from "@/utils/caravanaHelper";

const state = {
  animalesCria: [],
  paginacionCria: { total: 0, pagina: 1, limite: 20, total_paginas: 1 },
  resumenPendientesCria: null,
  lotesCria: [],
  loadingCria: false,
};

const getters = {
  animalesCria: (state) => state.animalesCria,
  paginacionCria: (state) => state.paginacionCria,
  resumenPendientesCria: (state) => state.resumenPendientesCria,
  lotesCria: (state) => state.lotesCria,
  loadingCria: (state) => state.loadingCria,
};

const mutations = {
  SET_ANIMALES(state, payload) {
    state.animalesCria = payload.items || [];
    state.paginacionCria = {
      total: payload.total || 0,
      pagina: payload.pagina || 1,
      limite: payload.limite || 20,
      total_paginas: payload.total_paginas || 1,
    };
  },
  SET_RESUMEN_PENDIENTES(state, payload) {
    state.resumenPendientesCria = payload;
  },
  SET_LOTES(state, payload) {
    state.lotesCria = payload || [];
  },
  SET_LOADING(state, val) {
    state.loadingCria = val;
  },
  UPDATE_ANIMAL_REPRODUCTOR(state, { animalId, esReproductor }) {
    const animal = state.animalesCria.find((a) => a.id === animalId);
    if (animal) {
      animal.es_reproductor = esReproductor;
    }
  },
};

const actions = {
  async getAnimalesCria({ commit }, params) {
    commit("SET_LOADING", true);
    try {
      const { data } = await apiTamboUrl.get("/cria/animales", { params });
      commit("SET_ANIMALES", data);

      // Guardar en Dexie para offline (igual que vacasStore de tambo)
      if (Array.isArray(data?.items) && data.items.length > 0) {
        const toSave = data.items.map((a) => ({
          ...a,
          lote_id: a.lote_id || a.lote_actual?.lote_id,
        }));
        db.animales.bulkPut(toSave).catch(() => {});
      }

      return data;
    } catch (error) {
      if (!navigator.onLine) {
        console.log("📴 Offline fallback: leyendo animales de cría de Dexie");
        const local = await db.animales.toArray();
        let items = local.filter(
          (a) => !a.en_feedlot && !a.vendido && a.estado_excepcion !== "muerto"
        );
        if (params?.sexo) {
          const s = String(params.sexo).toLowerCase().trim();
          items = items.filter(
            (a) => String(a.sexo || "").toLowerCase().trim() === s
          );
        }
        if (params?.lote_id) {
          items = items.filter(
            (a) => (a.lote_id || a.lote_actual?.lote_id) == params.lote_id
          );
        }
        if (params?.busqueda) {
          const b = String(params.busqueda).toLowerCase().trim();
          const norm = normalizarCaravana(b);
          items = items.filter(
            (a) =>
              String(a.caravana || "").toLowerCase().includes(b) ||
              (norm && normalizarCaravana(a.caravana) === norm)
          );
        }
        const localData = {
          items,
          total: items.length,
          pagina: 1,
          limite: items.length || 1000,
          total_paginas: 1,
        };
        commit("SET_ANIMALES", localData);
        return localData;
      }
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  /**
   * Búsqueda de solo lectura (no pisa `animalesCria` del rodeo). Usada por la inscripción
   * de vientres en campañas de eficiencia reproductiva y modales de tacto.
   */
  async buscarAnimalesCria(_, params) {
    try {
      const { data } = await apiTamboUrl.get("/cria/animales", { params });
      return data;
    } catch (error) {
      if (!navigator.onLine) {
        const local = await db.animales.toArray();
        let items = local.filter((a) => !a.en_feedlot && !a.vendido);
        if (params?.busqueda) {
          const b = String(params.busqueda).toLowerCase().trim();
          const norm = normalizarCaravana(b);
          items = items.filter(
            (a) =>
              String(a.caravana || "").toLowerCase().includes(b) ||
              (norm && normalizarCaravana(a.caravana) === norm)
          );
        }
        return { items, total: items.length };
      }
      throw error;
    }
  },
  /**
   * Contadores de las tarjetas del tablero en un solo pedido: `GET /cria/pendientes/tarjetas`
   * devuelve `conteos[categoria][subcategoria]` con las mismas reglas que la lista del modal
   * (`/grilla_vaca/pendientes`). Devuelve `{ clave: n }`; si el pedido falla, todas quedan en
   * `null` y la vista conserva su cálculo anterior.
   */
  async contarTarjetasDashboard(_, { tarjetas } = {}) {
    const lista = tarjetas || TARJETAS_DASHBOARD_CRIA;
    try {
      const { data } = await apiTamboUrl.get("/cria/pendientes/tarjetas");
      const conteos = data?.conteos || {};
      return Object.fromEntries(
        lista.map((t) => {
          const n = conteos[t.categoria]?.[t.subcategoria];
          return [t.clave, typeof n === "number" ? n : null];
        })
      );
    } catch {
      return Object.fromEntries(lista.map((t) => [t.clave, null]));
    }
  },
  async getResumenPendientes({ commit }) {
    try {
      const { data } = await apiTamboUrl.get("/cria/pendientes");
      commit("SET_RESUMEN_PENDIENTES", data);
      return data;
    } catch (error) {
      if (!navigator.onLine) {
        console.log("📴 Offline: resumen pendientes cría fallback");
        const fallback = {
          tacto_preservicio: 0,
          listos_servicio: 0,
          observacion_celo: 0,
          tacto_prenez: 0,
          proximas_parto: 0,
          preniadas: 0,
          terneros_al_pie: 0,
          destetes_pendientes: 0,
          capados_pendientes: 0,
          toros_adultos_monta: 0,
          sugerencias_venta: 0,
        };
        commit("SET_RESUMEN_PENDIENTES", fallback);
        return fallback;
      }
      throw error;
    }
  },
  async getLotesCria({ commit }) {
    try {
      const { data } = await apiTamboUrl.get("/cria/lotes");
      commit("SET_LOTES", data);

      if (Array.isArray(data) && data.length > 0) {
        const normalizados = data.map((l) => ({
          ...l,
          id: l.lote_id || l.id,
          nombre: l.nombre_lote || l.nombre,
        }));
        db.lotes.bulkPut(normalizados).catch(() => {});
      }

      return data;
    } catch (error) {
      if (!navigator.onLine) {
        console.log("📴 Offline fallback: leyendo lotes de cría de Dexie");
        const local = await db.lotes.toArray();
        const lotesNormalizados = local.map((l) => ({
          ...l,
          lote_id: l.lote_id || l.id,
          nombre_lote:
            l.nombre_lote || l.nombre || `Potrero #${l.lote_id || l.id}`,
        }));
        commit("SET_LOTES", lotesNormalizados);
        return lotesNormalizados;
      }
      throw error;
    }
  },
  async toggleReproductor({ commit }, animalId) {
    try {
      const { data } = await apiTamboUrl.post(
        `/grilla_vaca/${animalId}/toggle-reproductor`
      );
      commit("UPDATE_ANIMAL_REPRODUCTOR", {
        animalId,
        esReproductor: data.es_reproductor,
      });
      return data;
    } catch (error) {
      throw error;
    }
  },
  async registrarRecorridoLote(_, { loteId, payload }) {
    if (!navigator.onLine) {
      await enqueueOp(
        "recorridos_lote",
        "create",
        payload,
        `/cria/recorrido/${loteId}`,
        "POST"
      );
      return { _offline: true, ...payload, loteId };
    }
    const { data } = await apiTamboUrl.post(
      `/cria/recorrido/${loteId}`,
      payload
    );
    return data;
  },
  async getRecorridosLote(_, { loteId, params = {} }) {
    if (!navigator.onLine) {
      return { total: 0, items: [], pagina: 1, paginas: 1 };
    }
    const { data } = await apiTamboUrl.get(`/cria/recorridos/${loteId}`, {
      params,
    });
    return data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
